import Combobox from "@/components/core/forms/combobox";
import { Form, FormField, FormGroup, FormItem, FormLabel, FormMessage } from "@/components/core/forms/form";
import { Input } from "@/components/core/forms/input";
import Select from "@/components/core/forms/select";
import { philippineIslandGroups, philippineRegions, regionToIslandGroup } from "@/constants/philippines-regions";
import projectService from "@/features/dashboard/api/services/project-service";
import { type Site, siteSchema } from "@/features/dashboard/types/site";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";

interface SiteFormProps {
	site?: Site;
	onSubmit: (values: Site) => Promise<void>;
	isSubmitting?: boolean;
}

export function SiteForm({ site, onSubmit }: SiteFormProps) {
	const form = useForm<Site>({
		resolver: zodResolver(siteSchema),
		defaultValues: site
			? {
					...site,
					project_id: site.project_id || null,
				}
			: {
					id: "",
					client_id: "",
					site_code: "",
					description: "",
					project_id: null,
					project_name: null,
					latitude: "",
					longitude: "",
					plus_code: "",
					kit_number: "",
					address: "",
					city: "",
					region: "",
					island: "",
					location_name: "",
					locality: "",
					province: "",
					country: "Philippines",
					access_point: "",
					mbr: "",
					availability: null,
				},
	});

	return (
		<Form {...form}>
			<form id="site-form" className="flex flex-col w-full gap-6" onSubmit={form.handleSubmit(onSubmit)}>
				<div className="flex flex-col w-full gap-6">
					<div className="grid grid-cols-12 w-full gap-x-4 gap-y-3">
						<div className="col-span-12">
							<FormField
								control={form.control}
								name="project_id"
								render={({ field }) => (
									<FormItem>
										<FormLabel>Project Name</FormLabel>
										<FormGroup>
											<Combobox
												defaultValue={field.value?.toString()}
												placeholderValue={
													site?.project_id && site?.project_name
														? {
																value: site.project_id.toString(),
																label: site.project_name,
															}
														: undefined
												}
												onValueChange={(value) => {
													field.onChange(value ? Number(value) : null);
												}}
												loadOptions={async (value: string) => {
													if (!value || value.length === 0) {
														// Load all projects when no search term
														const response = await projectService.list();
														return response.data.map((project) => ({
															label: project.name,
															value: project.id.toString(),
														}));
													}
												}}
												placeholder="Select a project..."
											/>
										</FormGroup>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
					</div>

					<div className="grid grid-cols-12 w-full gap-x-4 gap-y-3">
						<div className="col-span-6">
							<FormField
								control={form.control}
								name="site_code"
								render={({ field }) => (
									<FormItem>
										<FormLabel required>Site Code</FormLabel>
										<FormGroup>
											<Input className="input" placeholder="Enter site code" {...field} />
										</FormGroup>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
						<div className="col-span-6">
							<FormField
								control={form.control}
								name="kit_number"
								render={({ field }) => (
									<FormItem>
										<FormLabel required>Kit Number</FormLabel>
										<FormGroup>
											<Input className="input" placeholder="Enter kit number" {...field} />
										</FormGroup>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
					</div>

					<div className="grid grid-cols-12 w-full gap-x-4 gap-y-3">
						<div className="col-span-4">
							<FormField
								control={form.control}
								name="latitude"
								render={({ field }) => (
									<FormItem>
										<FormLabel required>Latitude</FormLabel>
										<FormGroup>
											<Input
												className="input"
												step="any"
												type="number"
												placeholder="Enter latitude"
												{...field}
												onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : null)}
											/>
										</FormGroup>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
						<div className="col-span-4">
							<FormField
								control={form.control}
								name="longitude"
								render={({ field }) => (
									<FormItem>
										<FormLabel required>Longitude</FormLabel>
										<FormGroup>
											<Input
												className="input"
												step="any"
												type="number"
												placeholder="Enter longitude"
												{...field}
												onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : null)}
											/>
										</FormGroup>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
						<div className="col-span-4">
							<FormField
								control={form.control}
								name="plus_code"
								render={({ field }) => (
									<FormItem>
										<FormLabel required>Plus Code</FormLabel>
										<FormGroup>
											<Input className="input" placeholder="Enter plus code" {...field} />
										</FormGroup>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
					</div>

					<div className="grid grid-cols-12 w-full gap-x-4 gap-y-3">
						<div className="col-span-6">
							<FormField
								control={form.control}
								name="access_point"
								render={({ field }) => (
									<FormItem>
										<FormLabel>Access Point</FormLabel>
										<FormGroup>
											<Input className="input" placeholder="Enter access point details" {...field} />
										</FormGroup>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
						<div className="col-span-6">
							<FormField
								control={form.control}
								name="mbr"
								render={({ field }) => (
									<FormItem>
										<FormLabel>Gateway/Controller</FormLabel>
										<FormGroup>
											<Input className="input" placeholder="Enter Gateway/Controller details" {...field} />
										</FormGroup>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
					</div>

					<div className="grid grid-cols-12 w-full gap-x-4 gap-y-3">
						<div className="col-span-6">
							<FormField
								control={form.control}
								name="region"
								render={({ field }) => (
									<FormItem>
										<FormLabel required>Region</FormLabel>
										<FormGroup>
											<Select
												placeholder="Select region"
												options={philippineRegions}
												defaultValue={field.value}
												onValueChange={(value) => {
													field.onChange(value);
													// Auto-set island group based on region
													if (value && regionToIslandGroup[value]) {
														form.setValue("island", regionToIslandGroup[value]);
													}
												}}
											/>
										</FormGroup>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
						<div className="col-span-6">
							<FormField
								control={form.control}
								name="island"
								render={({ field }) => (
									<FormItem>
										<FormLabel required>Island Group</FormLabel>
										<FormGroup>
											<Select placeholder="Select island group" options={philippineIslandGroups} defaultValue={field.value} onValueChange={field.onChange} />
										</FormGroup>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
					</div>

					<div className="grid grid-cols-12 w-full gap-x-4 gap-y-3">
						<div className="col-span-6">
							<FormField
								control={form.control}
								name="locality"
								render={({ field }) => (
									<FormItem>
										<FormLabel required>Locality</FormLabel>
										<FormGroup>
											<Input className="input" placeholder="Enter locality" {...field} />
										</FormGroup>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
						<div className="col-span-6">
							<FormField
								control={form.control}
								name="province"
								render={({ field }) => (
									<FormItem>
										<FormLabel required>Province</FormLabel>
										<FormGroup>
											<Input className="input" placeholder="Enter province" {...field} />
										</FormGroup>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
					</div>

					<div className="grid grid-cols-12 w-full gap-x-4 gap-y-3">
						<div className="col-span-6">
							<FormField
								control={form.control}
								name="city"
								render={({ field }) => (
									<FormItem>
										<FormLabel>City</FormLabel>
										<FormGroup>
											<Input className="input" placeholder="Enter city" {...field} />
										</FormGroup>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
						<div className="col-span-6">
							<FormField
								control={form.control}
								name="country"
								render={({ field }) => (
									<FormItem>
										<FormLabel>Country</FormLabel>
										<FormGroup>
											<Input className="input" placeholder="Enter country" {...field} />
										</FormGroup>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
					</div>

					<div className="grid grid-cols-12 w-full gap-x-4 gap-y-3">
						<div className="col-span-12">
							<FormField
								control={form.control}
								name="location_name"
								render={({ field }) => (
									<FormItem>
										<FormLabel required>Location Name</FormLabel>
										<FormGroup>
											<Input className="input" placeholder="Enter location name" {...field} />
										</FormGroup>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
					</div>
				</div>
			</form>
		</Form>
	);
}
